import React from 'react';

import classNames from 'classnames';
import moment from 'moment';

import ActualsDashboardTile, { TileAction } from './ActualsDashboardTile';

import { compact, decimal, indicator } from 'Helpers/currencyFormat';
import { formatDateWithTz } from 'Helpers/dateUtils';

import Badge from 'CommonComponents/Badge';

const money = (value) => indicator(compact(Number(value) || 0));
const count = (value) => decimal(Number(value) || 0, 0);

const percent = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

// Widths are per-tile proportions, so they have to be inline — Tailwind cannot
// carry an arbitrary runtime percentage.
const barWidth = (value, max) => ({ width: `${max > 0 ? Math.max((Math.abs(value) / max) * 100, 1) : 0}%` });

// The server sends Y-m-d H:i:s (Carbon::toDateTimeString()), which is
// formatDateWithTz's default input format — the third argument is the one that
// sets how it reads back out.
const dateTime = (value) => (value ? formatDateWithTz(value, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY · HH:mm') : $t('Never'));

// Second argument is the INPUT format, not the output one.
const dateOnly = (value) => (value ? formatDateWithTz(value, 'YYYY-MM-DD', 'DD/MM/YYYY') : $t('None'));

// 'YYYY-MM' → 'Sep'. Parsed with moment directly rather than formatDateWithTz:
// that applies a timezone shift, which on a month boundary would roll the label
// back into the previous month.
const monthLabel = (month) => moment(month, 'YYYY-MM').format('MMM');

/** Shared by the tiles whose whole body is a list that can legitimately be empty. */
function TileEmptyState({ message }) {
    return <p className="tw-py-6 tw-text-center tw-text-xs tw-text-charcoal-50">{message}</p>;
}

function Row({ label, children }) {
    return (
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-3 tw-border-b tw-border-charcoal-15 tw-py-1.5 last:tw-border-b-0">
            <span className="tw-text-xs tw-text-charcoal-75">{label}</span>
            <span className="tw-text-xs tw-font-medium tw-text-charcoal tw-text-end">{children}</span>
        </div>
    );
}

/**
 * A labelled horizontal bar — the shared shape behind the by-department tile.
 */
function HBar({ label, value, max, emphasis }) {
    return (
        <div className="tw-flex tw-items-center tw-gap-x-2 tw-py-1">
            <span
                className={classNames('tw-w-28 tw-shrink-0 tw-truncate tw-text-2xs', emphasis ? 'tw-font-medium tw-text-red' : 'tw-text-charcoal-75')}
                title={label}
            >
                {label}
            </span>
            <span className="tw-h-3.5 tw-flex-auto tw-overflow-hidden tw-rounded tw-bg-charcoal-15">
                <span className={classNames('tw-block tw-h-full tw-rounded', emphasis ? 'tw-bg-red-75' : 'tw-bg-blue')} style={barWidth(value, max)} />
            </span>
            <span className={classNames('tw-w-16 tw-shrink-0 tw-text-end tw-text-2xs tw-font-medium', emphasis && 'tw-text-red')}>{money(value)}</span>
        </div>
    );
}

/**
 * Share of value reconciled.
 *
 * Reads `reconciliation_progress` straight through — it is the same
 * ActualCostsService::getActualCostsTotalData() payload the totals table under
 * this dashboard renders, and nothing here re-derives the split.
 */
export function ReconciliationProgressTile({ data, onNavigate }) {
    const totals = data?.reconciliation_progress ?? {};
    const overall = totals.total ?? {};
    const reconciled = Number(overall.reconciled?.amount) || 0;
    const unreconciled = Number(overall.unreconciled?.amount) || 0;
    const share = percent(reconciled, reconciled + unreconciled);

    const line = (label, group) => {
        const done = Number(group?.reconciled?.number) || 0;
        const all = Number(group?.total?.number) || 0;
        const byValue = percent(Number(group?.reconciled?.amount) || 0, (Number(group?.reconciled?.amount) || 0) + (Number(group?.unreconciled?.amount) || 0));

        return <Row label={label}>{$t('{{done}} / {{all}} items · {{percent}}% by value', { done: count(done), all: count(all), percent: byValue })}</Row>;
    };

    return (
        <ActualsDashboardTile
            title={$t('Reconciliation progress')}
            action={<TileAction label={$t('Unreconciled')} onClick={() => onNavigate('unreconciled')} />}
        >
            <div className="tw-text-2.5xl tw-font-semibold tw-text-charcoal">
                {share}% <span className="tw-text-xs tw-font-normal tw-text-charcoal-50">{$t('of value reconciled')}</span>
            </div>
            <div className="tw-mt-2 tw-h-2 tw-overflow-hidden tw-rounded tw-bg-charcoal-15">
                <div className="tw-h-full tw-rounded tw-bg-green" style={{ width: `${share}%` }} />
            </div>
            <div className="tw-mt-1 tw-flex tw-justify-between tw-text-2xs tw-text-charcoal-75">
                <span>{$t('{{amount}} reconciled', { amount: money(reconciled) })}</span>
                <span>{$t('{{amount}} unreconciled', { amount: money(unreconciled) })}</span>
            </div>
            <div className="tw-mt-3">
                {line($t('Revenue'), totals.revenue)}
                {line($t('Expenditure'), totals.expenditure)}
            </div>
        </ActualsDashboardTile>
    );
}

export function ImportHealthTile({ data, onNavigate }) {
    const health = data?.import_health ?? {};
    const unallocated = Number(health.auto_populated_no_department_count) || 0;

    return (
        <ActualsDashboardTile title={$t('Import health')} action={<TileAction label={$t('Unreconciled')} onClick={() => onNavigate('unreconciled')} />}>
            <Row label={$t('Last import')}>{dateTime(health.last_import_at)}</Row>
            <Row label={$t('Items received')}>{count(health.items_received)}</Row>
            <Row label={$t('Imported without a department')}>
                <Badge variant={unallocated > 0 ? 'danger' : 'success'} size="sm">
                    {unallocated > 0 ? $t('{{count}} items', { count: unallocated }) : $t('None')}
                </Badge>
            </Row>
            <Row label={$t('Next scheduled')}>{health.next_scheduled_import_at ? dateTime(health.next_scheduled_import_at) : $t('Not scheduled')}</Row>
            <Row label={$t('Earliest transaction date')}>{dateOnly(health.earliest_transaction_at)}</Row>
            <Row label={$t('Most recent transaction date')}>{dateOnly(health.latest_transaction_at)}</Row>
        </ActualsDashboardTile>
    );
}

// Value unreconciled by days since the transaction. Green / amber / red run
// from freshest to most overdue, matching the Badge variants used elsewhere.
const AGE_BUCKETS = [
    { key: '0_30', label: '0–30 DAYS', className: 'tw-bg-green-25 tw-text-dark-green' },
    { key: '31_60', label: '31–60 DAYS', className: 'tw-bg-amber-100 tw-text-amber-700' },
    { key: '60_plus', label: '60+ DAYS', className: 'tw-bg-red-25 tw-text-red-600' },
];

export function AgeingTile({ data, onNavigate }) {
    const ageing = data?.ageing ?? {};

    return (
        <ActualsDashboardTile title={$t('Unreconciled ageing')} action={<TileAction label={$t('View')} onClick={() => onNavigate('unreconciled')} />}>
            <div className="tw-flex tw-gap-x-2">
                {AGE_BUCKETS.map((bucket) => (
                    <button
                        key={bucket.key}
                        type="button"
                        onClick={() => onNavigate('unreconciled')}
                        className={classNames('tw-flex-1 tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-center', bucket.className)}
                    >
                        <span className="tw-block tw-text-base tw-font-semibold">{money(ageing[bucket.key]?.amount)}</span>
                        <span className="tw-mt-0.5 tw-block tw-text-3xs tw-font-medium tw-tracking-wide">{$t(bucket.label)}</span>
                        <span className="tw-mt-0.5 tw-block tw-text-3xs">{$t('{{count}} items', { count: Number(ageing[bucket.key]?.count) || 0 })}</span>
                    </button>
                ))}
            </div>
        </ActualsDashboardTile>
    );
}

/**
 * Revenue and expenditure are kept in separate lists because revdepartments and
 * departments are separate tables — a shared name is a coincidence, not a match.
 */
export function ByDepartmentTile({ data, onNavigate }) {
    const breakdown = data?.unreconciled_by_department ?? {};

    const list = (label, rows, type) => {
        const max = Math.max(...(rows || []).map((row) => Math.abs(Number(row.amount) || 0)), 0);

        if (!rows?.length) {
            return null;
        }

        return (
            <div className="tw-mt-2 first:tw-mt-0">
                <button
                    type="button"
                    onClick={() => onNavigate('unreconciled', type)}
                    className="tw-text-2xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-charcoal-50 hover:tw-text-blue"
                >
                    {label}
                </button>
                {rows.map((row) => (
                    <HBar
                        key={`${type}-${row.department_id ?? 'unallocated'}`}
                        label={row.name}
                        value={row.amount}
                        max={max}
                        emphasis={row.department_id === null}
                    />
                ))}
            </div>
        );
    };

    return (
        <ActualsDashboardTile title={$t('Unreconciled by department')} action={<TileAction label={$t('Filter')} onClick={() => onNavigate('unreconciled')} />}>
            {/* Same shape as top suppliers: both lists are empty once everything
                is reconciled, which is a result worth stating rather than a
                blank panel. */}
            {!breakdown.expenditure?.length && !breakdown.revenue?.length && <TileEmptyState message={$t('Nothing unreconciled')} />}
            {list($t('Expenditure'), breakdown.expenditure, 'expenditure')}
            {list($t('Revenue'), breakdown.revenue, 'revenue')}
        </ActualsDashboardTile>
    );
}

export function TopSuppliersTile({ data, onNavigate }) {
    const suppliers = data?.top_suppliers ?? [];

    return (
        <ActualsDashboardTile
            title={$t('Top suppliers — unmatched actuals')}
            action={<TileAction label={$t('View')} onClick={() => onNavigate('unreconciled')} />}
        >
            {/* Legitimately empty when every unreconciled row arrived without a
                supplier — not the same as the dashboard having no data at all,
                which is handled one level up. */}
            {suppliers.length === 0 && <TileEmptyState message={$t('No supplier named on any unreconciled transaction')} />}
            {suppliers.map((supplier) => (
                <Row key={supplier.supplier} label={<span className="tw-block tw-max-w-[11rem] tw-truncate">{supplier.supplier}</span>}>
                    <span className="tw-text-charcoal-50 tw-font-normal tw-me-2">{$t('{{count}} items', { count: Number(supplier.count) || 0 })}</span>
                    {money(supplier.amount)}
                </Row>
            ))}
        </ActualsDashboardTile>
    );
}

/**
 * Expenditure actuals against the Latest Estimate's cashflow profile.
 *
 * The current month's actuals bar is dimmed: it is a part-month against a whole
 * month of forecast, so reading it as "under profile" would be wrong.
 */
export function CashflowComparisonTile({ data }) {
    const comparison = data?.cashflow_comparison ?? {};
    const months = comparison.months ?? [];
    const max = Math.max(...months.flatMap((month) => [Number(month.actual) || 0, Number(month.forecast) || 0]), 0);

    const height = (value) => ({ height: `${max > 0 ? Math.max((Math.abs(Number(value) || 0) / max) * 100, 2) : 0}%` });

    return (
        <ActualsDashboardTile title={$t('Actuals vs cashflow forecast')}>
            <div className="tw-flex tw-h-28 tw-items-end tw-gap-x-2">
                {months.map((month) => (
                    <div key={month.month} className="tw-flex tw-h-full tw-flex-1 tw-items-end tw-gap-x-0.5">
                        <span
                            className="tw-flex-1 tw-rounded-t tw-bg-blue-25"
                            style={height(month.forecast)}
                            title={$t('Forecast: {{amount}}', { amount: money(month.forecast) })}
                        />
                        <span
                            className={classNames('tw-flex-1 tw-rounded-t tw-bg-blue', month.partial && 'tw-opacity-40')}
                            style={height(month.actual)}
                            title={$t('Actuals: {{amount}}', { amount: money(month.actual) })}
                        />
                    </div>
                ))}
            </div>
            <div className="tw-mt-1.5 tw-flex tw-gap-x-2">
                {months.map((month) => (
                    <span key={month.month} className="tw-flex-1 tw-text-center tw-text-3xs tw-text-charcoal-50">
                        {monthLabel(month.month)}
                        {month.partial ? ` ${$t('(part)')}` : ''}
                    </span>
                ))}
            </div>
            <div className="tw-mt-3 tw-flex tw-gap-x-4 tw-text-2xs tw-text-charcoal-75">
                <span className="tw-flex tw-items-center tw-gap-x-1.5">
                    <span className="tw-h-2.5 tw-w-2.5 tw-rounded-sm tw-bg-blue-25" />
                    {$t('Forecast')}
                </span>
                <span className="tw-flex tw-items-center tw-gap-x-1.5">
                    <span className="tw-h-2.5 tw-w-2.5 tw-rounded-sm tw-bg-blue" />
                    {$t('Actuals')}
                </span>
            </div>
        </ActualsDashboardTile>
    );
}

/**
 * Commit-from-actuals pipeline.
 *
 * Only the "to action" box is real — it counts allocations carrying
 * `sent_for_commitment_at`. Approval and rejection are not stored against an
 * allocation yet, so those two boxes are labelled as pending rather than shown
 * as zero, which would read as "nothing was rejected".
 */
export function CommitPipelineTile({ data, onNavigate }) {
    const pipeline = data?.commit_pipeline ?? {};

    return (
        <ActualsDashboardTile
            title={$t('Commit from actuals — pipeline')}
            action={<TileAction label={$t('Queue')} onClick={() => onNavigate('send_for_commitment')} />}
        >
            <div className="tw-flex tw-gap-x-2">
                <button
                    type="button"
                    onClick={() => onNavigate('send_for_commitment')}
                    className="tw-flex-1 tw-rounded-lg tw-border tw-border-grey tw-px-3 tw-py-2.5 tw-text-start"
                >
                    <span className="tw-block tw-text-lg tw-font-semibold tw-text-charcoal">{count(pipeline.to_action_count)}</span>
                    <span className="tw-mt-0.5 tw-block tw-text-2xs tw-text-charcoal-50">
                        {$t('To action · {{amount}}', { amount: money(pipeline.to_action_amount) })}
                    </span>
                </button>
                {[$t('Pending approval'), $t('Rejected')].map((label) => (
                    <div key={label} className="tw-flex-1 tw-rounded-lg tw-border tw-border-dashed tw-border-charcoal-25 tw-bg-charcoal-10 tw-px-3 tw-py-2.5">
                        <span className="tw-block tw-text-2xs tw-font-medium tw-text-charcoal-50">{$t('Coming soon')}</span>
                        <span className="tw-mt-0.5 tw-block tw-text-2xs tw-text-charcoal-50">{label}</span>
                    </div>
                ))}
            </div>
        </ActualsDashboardTile>
    );
}
